import { api } from '../api/client';
import type { AppFeedback, BabySex, PatientUser } from './types';

export type MaternityPhaseTarget = 'TENTANTE' | 'GESTANTE' | 'PUERPERA' | 'MAE_DE_ANJO' | 'ADOTANTE' | 'MAE';

/** Dependentes da titular (A062, fase E): ela vê só "convite enviado". */
export interface SituacaoDosDependentes {
  podeConvidar: boolean;
  motivo: string | null;
  limite: number;
  convites: Array<{ id: string; email: string; nomeInformado: string | null; createdAt: string; situacao: 'ENVIADO' }>;
}

/** O que falta na conta (A062, C2): pedido antes da home. */
export type PendenciaDaConta =
  | { tipo: 'DATA_DE_NASCIMENTO' }
  | { tipo: 'TERMOS'; versao: string; termosUrl: string; privacidadeUrl: string };

export const profileApi = {
  me: () => api.get<{ user: PatientUser }>('/api/auth/me').then((r) => r.user),
  update: (userId: string, data: Record<string, unknown>) => api.put<PatientUser>(`/api/users/${userId}`, data),

  pendencias: () => api.get<PendenciaDaConta[]>('/api/me/pendencias'),
  dependentes: () => api.get<SituacaoDosDependentes>('/api/me/dependentes'),
  convidarDependente: (nome: string, email: string) => api.post<SituacaoDosDependentes>('/api/me/dependentes', { nome, email }),
  cancelarDependente: (id: string) => api.delete<SituacaoDosDependentes>(`/api/me/dependentes/${id}`),
  /** Grava a data (AAAA-MM-DD) e/ou o aceite; devolve o que ainda falta. */
  resolverPendencias: (dados: { dateOfBirth?: string; aceiteTermos?: { versao: string } }) =>
    api.post<PendenciaDaConta[]>('/api/me/pendencias', dados),

  /** Troca de jornada — corpos como o mobile envia (profile.service.ts). */
  updateMaternityPhase(userId: string, phase: MaternityPhaseTarget, extra: { dateOfBirth?: string; lmpDate?: string | null; weeks?: number; days?: number; childbirthDate?: string; babies?: Array<{ name: string | null; sex: BabySex }> } = {}) {
    const agora = new Date().toISOString();
    if (phase === 'GESTANTE') {
      return profileApi.update(userId, {
        userType: 'PREGNANT',
        pregnancy: {
          status: 'active',
          lmpDate: extra.lmpDate ? { type: 'exact', date: extra.lmpDate } : { type: 'unknown', date: '' },
          enforcedGestationalAge: { weeks: extra.weeks ?? 0, days: extra.days ?? 0, referenceDate: agora },
          babies: [{ name: null, sex: 'UNKNOWN' }],
        },
      });
    }
    if (phase === 'PUERPERA') {
      return profileApi.update(userId, {
        userType: 'PREGNANT',
        pregnancy: {
          status: 'completed',
          childbirthDate: extra.childbirthDate ?? '',
          babies: extra.babies ?? [{ name: null, sex: 'UNKNOWN' }],
          lmpDate: { type: 'unknown', date: '' },
          enforcedGestationalAge: { weeks: 0, days: 0, referenceDate: agora },
        },
      });
    }
    // Virar tentante exige a data de nascimento (A062, C2); ao meio-dia UTC,
    // como no onboarding, para a data civil não virar o dia anterior.
    return profileApi.update(userId, { userType: phase, ...(extra.dateOfBirth ? { dateOfBirth: `${extra.dateOfBirth}T12:00:00Z` } : {}) });
  },

  updateGestationalAge: (userId: string, weeks: number, days: number) =>
    profileApi.update(userId, { pregnancy: { enforcedGestationalAge: { weeks, days, referenceDate: new Date().toISOString() } } }),

  /**
   * Informar a DUM de uma gestação em curso.
   *
   * `lmpDate` é uma data civil ("15 de janeiro"), não um instante: vai ao meio-dia
   * local para sobreviver ao fuso nas duas pontas. O servidor lê o dia em UTC
   * (`computeDueDate`) e o app lê o dia local (`computeGestationInfo`); à
   * meia-noite os dois discordariam em um dia.
   *
   * Zera a idade informada de propósito. `computeGestationInfo` prefere
   * `enforcedGestationalAge` quando ela vale 3 semanas ou mais, então uma idade
   * antiga continuaria mandando e a DUM recém-informada não apareceria em lugar
   * nenhum. Abaixo desse piso, a conta cai na DUM — que é o que se quer aqui.
   */
  updateLmpDate: (userId: string, lmpDateLocal: { year: number; month: number; day: number }) =>
    profileApi.update(userId, {
      pregnancy: {
        lmpDate: { type: 'exact', date: new Date(lmpDateLocal.year, lmpDateLocal.month - 1, lmpDateLocal.day, 12).toISOString() },
        enforcedGestationalAge: { weeks: 0, days: 0, referenceDate: new Date().toISOString() },
      },
    }),

  updateMarketingConsent: (userId: string, consent: { email: boolean; sms: boolean }, current?: { webpush?: boolean } | null) =>
    profileApi.update(userId, { marketingConsent: { email: consent.email, sms: consent.sms, webpush: current?.webpush ?? false } }),

  changePassword: (userId: string, currentPassword: string, newPassword: string) => api.post<void>(`/api/users/${userId}/change-password`, { currentPassword, newPassword }),
  /** Troca do próprio e-mail (14/10/2026): o código vai para o novo endereço; só troca ao confirmar. */
  pedirCodigoDeEmail: (email: string, senhaAtual: string) => api.post<{ email: string }>('/api/me/email/codigo', { email, senhaAtual }),
  confirmarEmail: (email: string, codigo: string) => api.post<PatientUser>('/api/me/email/confirmar', { email, codigo }),
  sendSharedAccessInvite: (email: string) => api.post<void>('/api/auth/shared-access-invite', { email }),
  softDelete: () => api.patch<void>('/api/me/profile/soft-delete'),

  feedbackMine: () => api.get<AppFeedback | null>('/api/app-feedback/me').catch(() => null),
  feedbackSubmit: (rating: number, comment?: string) => api.post<AppFeedback>('/api/app-feedback', { rating, comment }),
};
